import React from 'react';

function ConditionSelectionPanel({
  title,
  subtitle,
  selectedOptions,
  isMultiSelect,
  isConfirmed,
  isDisabled,
  onSelect,
  onConfirm,
  onChangeChoice,
  conditions,
  allowEmptyConfirm = false, // prop for allowing empty confirmations
  canEdit = true,
}) {

    const [showScrollHint, setShowScrollHint] = React.useState(true);
    const scrollContainerRef = React.useRef(null);

   // Hide scroll hint when user reaches bottom of container
const handleScroll = React.useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
  
    // Calculate if we're at the bottom
    const isAtBottom = 
      Math.abs(
        container.scrollHeight - container.scrollTop - container.clientHeight
      ) < 10; // Adding small threshold for better UX
  
    setShowScrollHint(!isAtBottom);
  }, []);
  
  React.useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      
      // Check if content is actually scrollable
      setShowScrollHint(scrollContainer.scrollHeight > scrollContainer.clientHeight);
    }
  
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

    
  return (
    <div className={`${isConfirmed ? 'bg-emerald-50' : 'bg-gray-50'} text-left rounded-2xl border border-gray-200 p-6 shadow-md`}>
      <div className="flex justify-between items-center">
        <div>
          <h4 className="font-semibold text-lg">{title}</h4>
          {subtitle && <p className="text-sm text-gray-600">{subtitle}</p>}
          <div className="text-violet-800 mt-2">
            {Array.isArray(selectedOptions) && selectedOptions.length > 0 ? (
              <ul className="space-y-1">
                {selectedOptions.map((option, i) => (
                  <li key={i} className="flex items-start gap-2 font-semibold">
                    <span className="text-violet-400 mt-0.5">•</span>
                    <span>{option}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <span className="font-semibold">{selectedOptions}</span>
            )}
          </div>
        </div>
        {canEdit && (
        <button
          onClick={isConfirmed ? onChangeChoice : onConfirm}
          className={`py-2 px-4 rounded-md shadow-md transition-all duration-300 font-semibold ${
            isConfirmed 
              ? 'flex items-center text-gray-900 border bg-white border-gray-300' 
              : (selectedOptions?.length || (allowEmptyConfirm && isConfirmed !== null) 
                  ? 'bg-violet-900 text-white' 
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed')
          }`}
          disabled={!isConfirmed && !selectedOptions?.length && !(allowEmptyConfirm && isConfirmed !== null)}
        >
          {isConfirmed ? 'Change Choice' : 'Confirm Choice'}
        </button>
        )}
      </div>

      {!isConfirmed && canEdit && (
        <div className="relative">
        <div 
          ref={scrollContainerRef}
          className="mt-2 overflow-y-auto max-h-[400px] scroll-smooth"
        >
          {Object.entries(conditions).map(([category, options]) => (
            <div key={category} className="mt-4 border border-gray-300 rounded-lg">
              <div className="rounded-lg p-4 bg-gray-100">
                <h3 className="text-sm font-semibold">{category}</h3>
              </div>
              <div className="rounded-lg p-4 bg-white">
                <table className="w-full">
                  <tbody>
                    {options.map((option) => {
                      const isSelected = isMultiSelect 
                        ? selectedOptions?.includes(option.icd_11_label)
                        : selectedOptions === option.icd_11_label;
                      const isOptionDisabled = isDisabled?.(option.icd_11_label);

                      return (
                        <tr
                          key={option.icd_11_label}
                          onClick={() => !isOptionDisabled && onSelect(option.icd_11_label)}
                          className={`
                            text-sm rounded-lg transition-all duration-300
                            ${isSelected
                              ? `bg-violet-300 outline outline-4 outline-violet-500 font-semibold ${isOptionDisabled ? 'cursor-not-allowed' : ''}`
                              : isOptionDisabled
                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                : 'hover:bg-violet-100 cursor-pointer'}
                          `}
                        >
                          <td className="py-2 pl-2">
                            {option.icd_11_label}
                            {title !== 'Provisional Primary Diagnosis' && option.symptomOverlapWithADHD && (
                              <span className="italic text-gray-600 text-xs ml-2">
                                (Symptom Overlap: {option.symptomOverlapWithADHD})
                              </span>
                            )}
                          </td>
                        </tr>
                      )}
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}                    
        </div>   
             
        {showScrollHint ? (
            <div className="mt-1 text-center pointer-events-none h-2">
              <span className="inline-flex items-center text-xs text-gray-600">          
                Scroll to see more conditions
              </span>
            </div>
          ):(
            <div className="mt-1 h-2"></div>
          )}
        </div>
      )}
            
      {!isConfirmed && !canEdit && (
        <div className="mt-4 p-3 bg-gray-100 rounded-md">
          <p className="text-sm text-gray-600 italic">
            No selections have been made.
          </p>
        </div>
      )}
    </div>
  );
}

export default ConditionSelectionPanel;